/**
 * Invitation Access Hook
 *
 * Provides access control based on invitation status.
 * Used to check if a user has been invited (has an inviter)
 * before allowing them to perform key actions.
 */

import { useQuery } from '@tanstack/react-query';

import { useAuthContext } from '@/context/AuthContext';
import * as referralApi from '@/api/referral';

export function useInvitationAccess() {
  const { isAuthenticated } = useAuthContext();

  // Check if user has an inviter (has been invited)
  const { data: accessData, isLoading } = useQuery({
    queryKey: ['accessCheck'],
    queryFn: async () => {
      const response = await referralApi.checkAccess();
      if (response.code !== 0) {
        throw new Error(response.message || 'Failed to check access');
      }
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds
  });

  const hasInviter = accessData?.has_inviter ?? false;
  const hasAccess = isAuthenticated && hasInviter;

  /**
   * Check if user has access (is invited)
   * @returns true if user has access, false otherwise
   */
  const checkAccess = (): boolean => {
    return hasAccess;
  };

  return {
    hasAccess,
    hasInviter,
    isLoading,
    checkAccess,
  };
}
